import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { useSelector } from "react-redux";

import footerConfig, { FooterConfigType } from "./opco-defaults/index";

import {
  Wrapper,
  ScrollTopButton,
  FooterContent,
  TopRow,
  LogoWrapper,
  LogoImage,
  NavList,
  FooterItem,
  BottomRow,
  CopyRightItem,
} from "./styles";

import getOpco from "utils/getOpco";
import { isFeatureActive } from "utils/getFeature";
import getImageUrl from "utils/getImageUrl";
import { userState } from "state/user-slice";

const opcoFooterConfig: FooterConfigType = footerConfig[getOpco()] || footerConfig.gb;

const isFooterVisible = (pathname: string) => {
  const isSpecialPage = pathname === "/" || pathname === "cookie-preferences" || pathname === "/myaccount";

  const isOfferPage =
    (pathname.includes("/offer/") || pathname.includes("/landingPage") || pathname.includes("/multiplanoffer/")) &&
    !pathname.includes("cancellation") &&
    !pathname.includes("pre-setup-form");

  const isFooterAlwaysVisible = isFeatureActive("Footer visible on all pages");
  const isNotElligible = pathname === "/not-eligible-user";

  return (isSpecialPage || isOfferPage || isFooterAlwaysVisible) && !isNotElligible;
};

const Footer = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isVisible = useMemo(() => isFooterVisible(pathname), [pathname]);
  const user = useSelector(userState);
  const opco = getOpco();

  const currentYear = new Date().getFullYear();
  const brandName =
    isFeatureActive("Enabled default theme") || isFeatureActive("Enabled CZ theme") ? "Vodafone" : "Lowi";

  const CopyRightText = `© ${currentYear} ${brandName} ${t(", All rights reserved")}`;
  const srcPath =
    brandName === "Lowi" ? "/images/icons/lowi/lowi_footer_logo_inverse.svg" : "/images/icons/vf_footer_logo.svg";

  const altText = brandName === "Lowi" ? "Lowi Logo" : "Vodafone Logo";

  const footerItems = [
    { key: "withdrawal", label: t("Withdrawal") },
    { key: "privacy-policy", label: t("Privacy policy") },
    { key: "cookie-preferences", label: t("Cookie preferences") },
    { key: "terms-and-conditions", label: t("Terms and conditions") },
    { key: "help-and-support", label: t("Help & support") },
    { key: "accessibility", label: t("Accessibility") },
    { key: "imprint", label: t("Imprint") },
  ];

  const handleScrollTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (!isVisible) return null;

  return (
    <Wrapper data-testid="footer">
      <ScrollTopButton type="button" onClick={handleScrollTop} aria-label={t("Back to top")} tabIndex={0}>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            d="M11.5 21.5002V3.70724L3.85348 11.3537C3.65822 11.549 3.34171 11.549 3.14645 11.3537C2.95118 11.1585 2.95118 10.842 3.14645 10.6467L11.6464 2.14669L11.7246 2.08224C11.9187 1.95407 12.1826 1.97583 12.3535 2.14669L20.8535 10.6467L20.9179 10.7248C21.0461 10.9189 21.0243 11.1829 20.8535 11.3537C20.6826 11.5246 20.4186 11.5463 20.2246 11.4182L20.1464 11.3537L12.5 3.70724V21.5002C12.5 21.7764 12.2761 22.0002 12 22.0002C11.7238 22.0002 11.5 21.7764 11.5 21.5002Z"
            fill="#111111"
            stroke="#101112"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ScrollTopButton>

      <FooterContent>
        <TopRow>
          <LogoWrapper>
            <LogoImage src={getImageUrl(srcPath)} alt={altText} data-testid="vf-footer-logo" />
          </LogoWrapper>

          <NavList>
            {footerItems.map((item) => {
              const opcoFooterItem = opcoFooterConfig[item.key as keyof typeof opcoFooterConfig];

              if (
                !opcoFooterItem ||
                (!user.loggedIn &&
                  (item.key === "cookie-preferences" ||
                    (item.key === "accessibility" && !(opco === "de" && pathname.includes("/landingPage")))))
              )
                return null;

              return (
                <FooterItem key={item.key} data-testid={`footer-${item.key}`}>
                  <Link to={opcoFooterItem} tabIndex={0}>
                    {item.label}
                  </Link>
                </FooterItem>
              );
            })}
          </NavList>
        </TopRow>

        <BottomRow>
          <CopyRightItem data-testid="footer-copyright">{CopyRightText}</CopyRightItem>
        </BottomRow>
      </FooterContent>
    </Wrapper>
  );
};

export default React.memo(Footer);
